/**
 * Session guard so a permanently missing asset cannot reload-loop the tab.
 * Kept for the tab lifetime (not cleared on successful boot).
 */
export const VITE_PRELOAD_RELOAD_STORAGE_KEY = 'luna:vite-preload-reloaded';

/**
 * Resolve the Error Vite attaches to `vite:preloadError` (see Vite `__vitePreload`).
 * @param {Event & { payload?: unknown }} event
 * @returns {Error}
 */
export function resolveVitePreloadError(event) {
  const payload = event?.payload;
  if (payload instanceof Error) {
    return payload;
  }
  if (typeof payload === 'string' && payload.length > 0) {
    return new Error(payload);
  }
  return new Error('Unable to preload module (Vite preload error)');
}

/**
 * Capture the preload failure in Sentry, suppress Vite's rethrow, and hard-reload once.
 *
 * Vite only throws when `defaultPrevented` is false; we preventDefault so the app can recover
 * via reload, and call `captureException` so the event is still recorded.
 *
 * @param {Event & { payload?: unknown }} event
 * @param {{
 *   captureException: (error: unknown) => void,
 *   reload?: () => void,
 *   storage?: Pick<Storage, 'getItem' | 'setItem'>,
 *   storageKey?: string
 * }} options
 */
export function handleVitePreloadError(event, options) {
  const {
    captureException,
    reload = () => {
      window.location.reload();
    },
    storage: configuredStorage,
    storageKey = VITE_PRELOAD_RELOAD_STORAGE_KEY
  } = options;

  captureException(resolveVitePreloadError(event));

  if (typeof event.preventDefault === 'function') {
    event.preventDefault();
  }

  try {
    const storage = configuredStorage === undefined ? sessionStorage : configuredStorage;
    if (storage.getItem(storageKey) !== '1') {
      storage.setItem(storageKey, '1');
      reload();
    }
  } catch {
    // Private mode / blocked storage: do not reload without a persisted guard.
  }
}

/**
 * Register the window listener. Call after Sentry.init so captureException is live.
 * No unsubscribe — registered once at app boot for the page lifetime.
 * @param {{ captureException: (error: unknown) => void }} options
 */
export function registerVitePreloadErrorHandler(options) {
  window.addEventListener('vite:preloadError', event => {
    handleVitePreloadError(event, options);
  });
}
